import * as React from "react"
import { X } from "lucide-react"

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./select"
import { cn } from "../../lib/utils"

type SelectOption = {
  value: string
  label: React.ReactNode
  disabled?: boolean
}

type FloatingLabelSelectProps = {
  id?: string
  label: React.ReactNode
  required?: boolean
  invalid?: boolean
  errorText?: React.ReactNode
  disabled?: boolean
  dir?: "rtl" | "ltr"
  value?: string
  options: SelectOption[]
  onValueChange?: (value: string) => void
  className?: string
  triggerClassName?: string
  dataTestId?: string
  allowClear?: boolean
  clearLabel?: React.ReactNode
}

const FloatingLabelSelect = React.forwardRef<HTMLButtonElement, FloatingLabelSelectProps>(
  (
    {
      id,
      label,
      required,
      invalid,
      errorText,
      disabled,
      dir = "rtl",
      value,
      options,
      onValueChange,
      className,
      triggerClassName,
      dataTestId,
      allowClear,
      clearLabel,
    },
    ref
  ) => {
    const generatedId = React.useId()
    const inputId = id ?? `floating-select-${generatedId}`
    const errorId = errorText ? `${inputId}-error` : undefined
    const [open, setOpen] = React.useState(false)
    const isRtl = dir === "rtl"
    const hasValue = Boolean(value)
    const shouldFloat = hasValue || open
    const showClear = allowClear && hasValue && !disabled

    return (
      <div className={cn("w-full", className)} dir={dir} data-testid={dataTestId}>
        <div className={cn("relative", open && "z-50")} data-invalid={invalid ? "true" : "false"}>
          <Select
            key={hasValue ? undefined : "cleared"}
            value={hasValue ? value : undefined}
            onValueChange={(v) => onValueChange?.(v)}
            disabled={disabled}
            dir={dir}
            open={open}
            onOpenChange={setOpen}
          >
            <SelectTrigger
              id={inputId}
              ref={ref}
              aria-invalid={invalid}
              aria-errormessage={invalid && errorId ? errorId : undefined}
              className={cn(
                "box-border h-10 w-full rounded-none border-0 border-b border-b-lupa-muted bg-lupa-surface px-3 pb-0.5 pt-3.5 font-ploni text-[18px] leading-none text-lupa-text shadow-none ring-0 focus:ring-0 focus:ring-offset-0 focus:border-b-lupa-brand focus-visible:border-b-lupa-brand",
                invalid && "border-b-lupa-error text-lupa-error focus-visible:border-b-lupa-error",
                "disabled:text-lupa-muted",
                triggerClassName
              )}
            >
              <SelectValue placeholder=" " />
            </SelectTrigger>
            <SelectContent
              align="start"
              className="max-h-64 border-[#e7eaee] shadow-[0_8px_24px_rgba(64,64,64,0.08)]"
            >
              {options.map((option) => (
                <SelectItem
                  key={String(option.value)}
                  value={String(option.value)}
                  disabled={option.disabled}
                  className="font_md color_black_olive"
                >
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {showClear && (
            <button
              type="button"
              aria-label={typeof clearLabel === "string" ? clearLabel : "Clear selection"}
              className={cn(
                "absolute top-3.5 bottom-0.5 z-20",
                "inline-flex w-6 items-center justify-center rounded-sm border-0 bg-transparent p-0",
                "text-lupa-muted hover:text-lupa-text hover:bg-lupa-hover",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                isRtl ? "left-7" : "right-7"
              )}
              onPointerDown={(e) => {
                e.preventDefault()
                e.stopPropagation()
              }}
              onClick={(e) => {
                e.stopPropagation()
                onValueChange?.("")
                setOpen(false)
              }}
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          )}

          <label
            htmlFor={inputId}
            style={{ insetInlineStart: "0.75rem" }}
            className={cn(
              "absolute z-10 select-none bg-transparent px-0 text-lupa-text text-start font-ploni",
              "transition-all duration-200 ease-out",
              shouldFloat
                ? "top-1.5 translate-y-0 scale-100 text-xs leading-none"
                : "top-1/2 -translate-y-1/2 scale-100 text-[14px] leading-none pointer-events-none",
              invalid && "text-lupa-error",
              disabled && "text-lupa-muted",
              isRtl ? "origin-top-right" : "origin-top-left"
            )}
          >
            {label}
            {required && (
              <span className={cn("ms-0.5", disabled ? "text-lupa-muted" : invalid ? "text-lupa-error" : "text-lupa-text")} aria-hidden="true">
                *
              </span>
            )}
          </label>
        </div>

        <p
          id={errorId}
          className={cn(
            "mt-1 min-h-[18px] text-[12px]",
            isRtl ? "text-right" : "text-left",
            invalid ? "text-lupa-error" : "invisible"
          )}
          role={invalid && errorText ? "alert" : undefined}
        >
          {invalid && errorText ? errorText : "\u00A0"}
        </p>
      </div>
    )
  }
)

FloatingLabelSelect.displayName = "FloatingLabelSelect"

export { FloatingLabelSelect }
